import { createRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { GdprErasureConfirmModal } from "../../components/custom/GdprErasureConfirmModal";
import { useGdprErasureMutation } from "../../hooks/useGdprErasureMutation";
import { ApiError } from "../../lib/apiClient";
import {
  GDPR_CASCADE_ENTITIES,
  type GdprErasureResponse,
} from "../../schemas/gdprErasure";

/**
 * /admin/gdpr-erasure — REQ-COMP-001 (Phase 32) GDPR Right to be Forgotten UI.
 *
 * Workflow (Petr admin trigger po obdržení subject access request):
 *   1. Petr zadá `userId` (UUID) + identifikační údaje (email/display_name/role)
 *      → tlačítko "Pokračovat na potvrzení" otevírá Confirm Modal.
 *   2. Confirm Modal 2-step: summary review → typed `erase <userId>` + 5s
 *      countdown → Submit.
 *   3. Po úspěchu se Modal zavře, page renderuje result card s per-entity
 *      counts + audit_log_entry_id pointer.
 *
 * NFR-021: < 72h SLA from request to completion. Cascade samotná se měří
 * v `duration_ms` z response — Petr commituje screenshot do
 * acceptance-results/F0-F1/at-023-gdpr-erasure-sla.md.
 */
export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/gdpr-erasure",
  component: GdprErasurePage,
});

function GdprErasurePage() {
  const [userId, setUserId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [role, setRole] = useState<string>("endUser");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [result, setResult] = useState<GdprErasureResponse | null>(null);
  const mutation = useGdprErasureMutation();

  // Po success automaticky zavři modal + zobraz result.
  useEffect(() => {
    if (mutation.isSuccess && mutation.data) {
      setResult(mutation.data);
      setModalOpen(false);
    }
  }, [mutation.isSuccess, mutation.data]);

  const userIdValid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    userId,
  );
  const formValid = userIdValid && email.length > 0 && displayName.length > 0;

  return (
    <section
      aria-labelledby="gdpr-title"
      data-testid="gdpr-erasure-page"
      className="max-w-7xl mx-auto space-y-6"
    >
      <header className="flex flex-col gap-1">
        <h2 id="gdpr-title" className="text-display-xs font-bold text-primary m-0">
          GDPR Právo být zapomenut
        </h2>
        <p className="text-md text-tertiary m-0">
          Manuální výmaz osobních údajů na základě žádosti subjektu údajů.
          SLA <strong className="text-primary">≤ 72 h</strong> od obdržení žádosti.{" "}
          <a
            href="/runbooks/gdpr-erasure.md"
            target="_blank"
            rel="noreferrer"
            className="text-brand-secondary hover:underline"
          >
            postup
          </a>
          .
        </p>
      </header>

      <Card>
        <CardHeader title="Identifikace cílového uživatele" />
        <CardBody>
          <div className="flex flex-col gap-4">
            <Input
              label="User ID (UUID)"
              isRequired
              data-testid="gdpr-erasure-userid-input"
              value={userId}
              onChange={setUserId}
              placeholder="00000000-0000-0000-0000-000000000000"
              autoComplete="off"
              isInvalid={userId.length > 0 && !userIdValid}
            />
            <Input
              label="Email (stav před výmazem)"
              isRequired
              data-testid="gdpr-erasure-email-input"
              value={email}
              onChange={setEmail}
              autoComplete="off"
            />
            <Input
              label="Jméno"
              isRequired
              data-testid="gdpr-erasure-name-input"
              value={displayName}
              onChange={setDisplayName}
              autoComplete="off"
            />
            <Input
              label="Role"
              data-testid="gdpr-erasure-role-input"
              value={role}
              onChange={setRole}
              autoComplete="off"
            />
          </div>
          <div className="mt-4">
            <Button
              color="primary"
              isDisabled={!formValid}
              onPress={() => setModalOpen(true)}
              data-testid="gdpr-erasure-open-modal-button"
            >
              Pokračovat na potvrzení
            </Button>
          </div>
        </CardBody>
      </Card>

      <GdprErasureConfirmModal
        open={modalOpen}
        userId={userId}
        email={email}
        displayName={displayName}
        role={role}
        submitting={mutation.isPending}
        submitError={mutation.error ? extractMessage(mutation.error) : null}
        onCancel={() => setModalOpen(false)}
        onConfirm={() => mutation.mutate({ userId })}
      />

      {result && (
        <Card data-testid="gdpr-erasure-result-card" className="mt-6">
          <CardHeader title="Výmaz proveden" />
          <CardBody>
            <InlineAlert variant="success" title="Výmaz dokončen">
              <p>
                <strong>Trvání:</strong> {result.duration_ms} ms
                <br />
                <strong>Dokončeno:</strong> {result.completed_at}
                <br />
                <strong>ID záznamu v auditu:</strong>{" "}
                <code data-testid="gdpr-erasure-audit-pointer">
                  {result.audit_log_entry_id}
                </code>
              </p>
            </InlineAlert>
            <h3 className="mt-4">Souhrn výmazu (6 oblastí)</h3>
            <table
              className="w-full border-collapse text-sm text-primary"
              data-testid="gdpr-erasure-counts-table"
            >
              <thead>
                <tr>
                  <th className="border-b border-secondary bg-secondary px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-tertiary">
                    Oblast
                  </th>
                  <th className="border-b border-secondary bg-secondary px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-tertiary">
                    Redigováno záznamů
                  </th>
                </tr>
              </thead>
              <tbody>
                {GDPR_CASCADE_ENTITIES.map((entity) => (
                  <tr key={entity} className="hover:bg-secondary">
                    <td className="border-b border-secondary px-4 py-3 text-left">
                      <code>{entity}</code>
                    </td>
                    <td className="border-b border-secondary px-4 py-3 text-left">
                      <Badge color="success">
                        {result.rows_redacted_by_entity[entity] ?? 0}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-4">
              <strong>Další krok:</strong> ulož snímek této stránky do{" "}
              <code>acceptance-results/F0-F1/at-023-gdpr-erasure-sla.md</code>.
            </p>
          </CardBody>
        </Card>
      )}
    </section>
  );
}

function extractMessage(err: unknown): string {
  if (err instanceof ApiError) return err.body?.message ?? err.message;
  if (err instanceof Error) return err.message;
  return "Neznámá chyba";
}
